/**
 * AccountSheet — the flat ☰ settings drawer. Headed by the household identity,
 * it is one ordered list (household-central → app-utility) + a danger-styled
 * Sign out alone at the bottom (T27.2.7.6 / MN1 flattened the former
 * Household/App grouping — at six rows the headings were pure chrome):
 *
 *   Account & members · Household preferences · Brand & product preferences
 *   · Store preferences · Your products · App settings
 *   ───────────────────────────────────────
 *   ⎋ Sign out
 *
 * Each entry routes to its surface (per-surface routing lives in App.tsx); the
 * surface's `‹ Menu` backlink returns here. The drawer is a `role="menu"` bottom
 * sheet on phone; the same component reads centered on desktop (the surfaces also
 * grow a left settings sidebar via SettingsLayover).
 *
 * Accessibility (T12.2 AC #1):
 *   - entries are `role="menuitem"` inside the `role="menu"` panel,
 *   - roving-arrow keyboard nav (↑/↓ move, Home/End jump, Enter/Space activate),
 *   - Escape + backdrop close,
 *   - focus moves into the panel (first item) on open, and returns to the ☰
 *     trigger on close.
 *
 * Native-feel polish (T27.2.5.3):
 *   - slide-up + backdrop-fade enter/exit on transform/opacity (D1) — the exit
 *     plays before unmount, gated by `prefers-reduced-motion` (CSS);
 *   - a drag-handle grabber + swipe-down-to-dismiss past a threshold (D2);
 *   - background scroll locked while open (D3, `useBodyScrollLock`);
 *   - the scrim reads from the `--color-overlay-scrim` token (B3) so it themes;
 *   - menu-row title is a block so the description drops to its own line (D6).
 *
 * All styles flow from tokens (CP5). No hex, no raw px literals.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '@shopista/shared/api/hooks';
import { useAuthActions } from '../auth/useAuthActions';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { useOverlayRegistration } from '../lib/useOverlayRegistration';
import { Icon, Modal } from '../components';
import { SETTINGS_MENU, type SettingsMenuEntry } from '../features/settings/settingsMenu';

// How long the exit animation runs before unmount — matches the panel
// `shopista-sheet-out` duration in responsive.css. A pure number so the
// component can drive an unmount timer (no token for animation timing).
const EXIT_MS = 200;
// Swipe-down distance (px) past which a drag dismisses the sheet. Below it, the
// sheet snaps back. ~96px ≈ a deliberate downward pull, not an accidental nudge.
const SWIPE_DISMISS_PX = 96;

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'var(--color-overlay-scrim)',
  zIndex: 'var(--z-modal)' as unknown as number,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const panelStyle: CSSProperties = {
  width: '100%',
  maxWidth: '32rem',
  maxHeight: '92%',
  overflowY: 'auto',
  backgroundColor: 'var(--color-bg-raised)',
  borderTopLeftRadius: 'var(--radius-lg)',
  borderTopRightRadius: 'var(--radius-lg)',
  padding: 'var(--space-4)',
  paddingBottom: 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

// Drag-handle grabber bar (D2) — a short rounded pill centered at the sheet's
// top edge, signalling "this is a sheet you can pull down". The row around it is
// the drag-capture surface (swipe-to-dismiss).
const grabberRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 'var(--space-1)',
  paddingBottom: 'var(--space-3)',
  marginTop: 'calc(-1 * var(--space-2))',
  touchAction: 'none',
  cursor: 'grab',
};

const grabberStyle: CSSProperties = {
  width: '2.25rem',
  height: '0.25rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-border-strong)',
};

const idStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-2) var(--space-1) var(--space-3)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  marginBottom: 'var(--space-1)',
};

const avatarStyle: CSSProperties = {
  width: '2.625rem',
  height: '2.625rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-lg)',
  flex: '0 0 auto',
};

const idNameStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const idSubStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  minHeight: '2.75rem',
  padding: 'var(--space-3)',
  textAlign: 'left',
  border: 'none',
  background: 'transparent',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
};

const rowIconStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  lineHeight: 1,
  width: '1.875rem',
  textAlign: 'center',
  flex: '0 0 auto',
};

const rowLabelStyle: CSSProperties = { flex: 1, minWidth: 0 };
// D6 — the title is a block so the description drops to its own line. Two inline
// `<span>`s flowed together as one paragraph (the description's marginTop was
// ignored on an inline box); `display:block` restores the intended two-line row.
const rowTitleStyle: CSSProperties = {
  display: 'block',
  fontWeight: 'var(--font-weight-semibold)',
};
const rowDescStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
  lineHeight: 'var(--line-height-tight)',
};
const chevStyle: CSSProperties = {
  marginLeft: 'auto',
  color: 'var(--color-text-muted)',
  flex: '0 0 auto',
};

const dividerStyle: CSSProperties = {
  height: '0.0625rem',
  backgroundColor: 'var(--color-border-subtle)',
  margin: 'var(--space-2) 0',
  border: 'none',
};

const dangerRowStyle: CSSProperties = {
  ...rowStyle,
  color: 'var(--color-status-danger)',
};

// Logout-confirm dialog body + actions. All token-driven (CP5): no hex, no px.
const confirmBodyStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const confirmActionsStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-5)',
};

const confirmButtonBaseStyle: CSSProperties = {
  minHeight: '2.75rem',
  padding: 'var(--space-2) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
  border: 'none',
};

const confirmCancelStyle: CSSProperties = {
  ...confirmButtonBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-primary)',
};

const confirmLogoutStyle: CSSProperties = {
  ...confirmButtonBaseStyle,
  backgroundColor: 'var(--color-status-danger)',
  color: 'var(--color-text-inverse)',
};

// The drawer renders the shared SETTINGS_MENU (MN1: one flat ordered list,
// household-central first → app-utility last; the divider before Sign out is
// the only break). Its `data-testid`s stay local because two diverge from the
// shared `key` and are test-asserted: `account` → `account-members`, `app` →
// `account-settings`; the middle four are `account-${key}`.
const ROW_TEST_IDS: Record<SettingsMenuEntry['key'], string> = {
  account: 'account-members',
  preferences: 'account-preferences',
  brands: 'account-brands',
  stores: 'account-stores',
  products: 'account-products',
  billing: 'account-billing',
  app: 'account-settings',
};

// Flat ordered list of every navigable entry (for roving-arrow nav). The Sign
// out row is appended last so arrow nav reaches it too.
const FLAT_ENTRY_IDS = [
  ...SETTINGS_MENU.map((e) => ROW_TEST_IDS[e.key]),
  'account-logout',
];

export interface AccountSheetProps {
  open: boolean;
  onClose: () => void;
}

export function AccountSheet({ open, onClose }: AccountSheetProps): JSX.Element | null {
  const navigate = useNavigate();
  const meQuery = useMe();
  const { logout } = useAuthActions();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The element focused before the drawer opened — restored on close so focus
  // returns to the ☰ trigger.
  const triggerRef = useRef<Element | null>(null);

  // Exit-before-unmount (D1): while the parent has `open` true we render open;
  // when it flips false we keep rendering in a `closing` phase for EXIT_MS so the
  // slide-down/fade animation plays, then unmount. `rendered` is the union.
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);
  const exitTimer = useRef<number | null>(null);

  // Live drag offset (px) while the user swipes the sheet down. Cleared on
  // release. Drives an inline translateY so the panel tracks the finger.
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  // Logout confirmation. Tapping Sign out opens this dialog; only confirming
  // runs logout. Cancel (or the Modal's own back/Escape/overlay dismiss — it
  // registers with the overlay stack so Android hardware Back closes it WITHOUT
  // logging out, T27.2.7.3) leaves the session intact.
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  useEffect(() => {
    if (open) {
      // (Re)open — cancel any pending exit, render immediately.
      if (exitTimer.current !== null) {
        window.clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
      setClosing(false);
      setDragY(0);
      setConfirmingLogout(false);
      setRendered(true);
      return undefined;
    }
    if (rendered) {
      // Parent closed us — play the exit, then unmount.
      setClosing(true);
      exitTimer.current = window.setTimeout(() => {
        setRendered(false);
        setClosing(false);
        exitTimer.current = null;
      }, EXIT_MS);
    }
    return () => {
      if (exitTimer.current !== null) {
        window.clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
    };
  }, [open, rendered]);

  // Lock background scroll while the sheet is on screen (D3) — including during
  // the exit animation, so the page can't scroll under a dismissing sheet.
  useBodyScrollLock(rendered);

  // T27.2.7.3 — register with the overlay stack while open so the Android
  // hardware Back button closes this menu instead of redirecting the page (MN2).
  // Keyed on `open` (the intent flag), not `rendered`, so the entry leaves the
  // stack as the close starts, not after the exit animation finishes.
  useOverlayRegistration(open, onClose);

  // Capture the opener + move focus into the panel on open; restore on close.
  useEffect(() => {
    if (!open) return undefined;
    triggerRef.current = document.activeElement;
    // Focus the first menu item once the panel mounts.
    const first = panelRef.current?.querySelector<HTMLElement>(
      '[role="menuitem"]'
    );
    first?.focus();
    return () => {
      // Return focus to the ☰ trigger when the drawer closes.
      const trigger = triggerRef.current;
      if (trigger instanceof HTMLElement) trigger.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ??
            []
        );
        if (items.length === 0) return;
        const activeIndex = items.findIndex((el) => el === document.activeElement);
        let next = 0;
        if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = items.length - 1;
        else if (e.key === 'ArrowDown') next = (activeIndex + 1) % items.length;
        else next = (activeIndex - 1 + items.length) % items.length;
        items[next]?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Swipe-to-dismiss (D2). The grabber row captures the pointer; a downward drag
  // tracks the finger, and a release past SWIPE_DISMISS_PX closes the sheet
  // (below it the sheet snaps back to 0).
  const onGrabberPointerDown = useCallback((e: ReactPointerEvent) => {
    dragStartY.current = e.clientY;
    setDragY(0);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onGrabberPointerMove = useCallback((e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.clientY - dragStartY.current;
    // Only track downward pulls; ignore upward drags (clamp at 0).
    setDragY(delta > 0 ? delta : 0);
  }, []);

  const onGrabberPointerUp = useCallback(() => {
    if (dragStartY.current === null) return;
    const shouldClose = dragY >= SWIPE_DISMISS_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  }, [dragY, onClose]);

  if (!rendered) return null;

  const user = meQuery.data;

  const goTo = (to: string) => {
    navigate(to);
    onClose();
  };

  // While dragging, follow the finger with an inline translateY (and suppress
  // the CSS slide animation so the two don't fight). Otherwise the panel class
  // drives the enter/exit animation.
  const dragging = dragY > 0;
  const panelDynamicStyle: CSSProperties = dragging
    ? { ...panelStyle, transform: `translateY(${dragY}px)`, animation: 'none' }
    : panelStyle;

  return (
    <div
      className={`shopista-sheet-overlay${closing ? ' is-closing' : ''}`}
      style={overlayStyle}
      data-testid="account-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="menu"
        aria-label="Account, household & settings"
        className={`shopista-sheet-panel${closing ? ' is-closing' : ''}`}
        style={panelDynamicStyle}
        data-testid="account-sheet"
      >
        <div
          style={grabberRowStyle}
          data-testid="account-grabber"
          aria-hidden="true"
          onPointerDown={onGrabberPointerDown}
          onPointerMove={onGrabberPointerMove}
          onPointerUp={onGrabberPointerUp}
          onPointerCancel={onGrabberPointerUp}
        >
          <span style={grabberStyle} />
        </div>

        <div style={idStyle}>
          <span style={avatarStyle} aria-hidden="true">
            <Icon name="home" data-testid="account-avatar-icon" />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={idNameStyle} data-testid="account-household">
              {user?.householdName ?? 'Your household'}
            </div>
            {user?.email && (
              <div style={idSubStyle} data-testid="account-email">
                {user.email}
              </div>
            )}
          </div>
        </div>

        {SETTINGS_MENU.map((entry) => {
          const testId = ROW_TEST_IDS[entry.key];
          return (
          <button
            key={testId}
            type="button"
            role="menuitem"
            tabIndex={FLAT_ENTRY_IDS[0] === testId ? 0 : -1}
            style={rowStyle}
            onClick={() => goTo(entry.to)}
            data-testid={testId}
          >
            <span style={rowIconStyle} aria-hidden="true">
              <Icon name={entry.icon} data-testid={`${testId}-icon`} />
            </span>
            <span style={rowLabelStyle}>
              <span style={rowTitleStyle}>{entry.label}</span>
              <span style={rowDescStyle}>{entry.desc}</span>
            </span>
            <span style={chevStyle} aria-hidden="true">
              <Icon name="chevron-right" />
            </span>
          </button>
          );
        })}

        <hr style={dividerStyle} />

        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          style={dangerRowStyle}
          onClick={() => setConfirmingLogout(true)}
          data-testid="account-logout"
        >
          <span style={rowIconStyle} aria-hidden="true">
            <Icon name="logout" data-testid="account-logout-icon" />
          </span>
          Sign out
        </button>
      </div>

      <Modal
        open={confirmingLogout}
        onClose={() => setConfirmingLogout(false)}
        title="Log out?"
      >
        <p style={confirmBodyStyle} data-testid="account-logout-confirm-body">
          You&rsquo;ll need to sign in again.
        </p>
        <div style={confirmActionsStyle}>
          <button
            type="button"
            style={confirmCancelStyle}
            onClick={() => setConfirmingLogout(false)}
            data-testid="account-logout-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            style={confirmLogoutStyle}
            onClick={() => {
              setConfirmingLogout(false);
              onClose();
              void logout();
            }}
            data-testid="account-logout-confirm"
          >
            Log out
          </button>
        </div>
      </Modal>
    </div>
  );
}
